import { ArrowRight, Database, LayoutGrid, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AREA_ADMIN, type ItemDeNavegacao } from '@/nucleo/navegacao/mapa';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { EmptyState } from '@/ui/EmptyState';

import { useResumoDosCadastros } from './cadastros.dados';
import { contagemDoDominio, HUBS, type ChaveDeContagem } from './hubs';

const RAIZ = '/admin/parametros';

type Hub = ItemDeNavegacao & { filhos: readonly Omit<ItemDeNavegacao, 'filhos'>[] };

/**
 * `/admin/parametros` — porta de entrada da parametrização.
 *
 * A LISTA DE HUBS NÃO É ESCRITA AQUI: sai do mapa de navegação, pelos itens que
 * têm `filhos` e descem de `/admin/parametros`. É o que impede esta tela de
 * divergir do menu lateral — os dois leem a mesma fonte, e um hub novo aparece
 * nos dois lugares sem tocar neste arquivo.
 *
 * O QUE ESTA TELA NÃO TEM, DE PROPÓSITO, e por dois motivos diferentes.
 *
 * O painel de "alterações recentes" existe no protótipo e mostra o histórico de
 * edição de parâmetro de toda a área. Os cadastros de referência já gravam (a
 * trilha `audit_log` recebe as alterações pelos gatilhos das próprias tabelas),
 * mas as demais áreas ainda não, e um painel que mostrasse só os cadastros
 * passaria a impressão de que o resto da parametrização não muda. O painel
 * entra quando as demais áreas tiverem escrita, e aí nasce com o histórico de
 * todas.
 *
 * Os indicadores de "saúde do módulo" e o painel de "tabelas que precisam de
 * atenção" dependem de regra de validação que não foi construída. Preenchê-los a
 * partir de dado simulado exibiria a quem audita um diagnóstico que ninguém
 * apurou — num sistema público isso é pior que a lacuna, e é a mesma decisão já
 * registrada em CLAUDE.md para a coluna `Saúde`.
 */
export function CentroDeParametrizacao() {
  const [busca, setBusca] = useState('');
  // Os cadastros que já leem a API contam pelo resumo do servidor; enquanto ele
  // não chega, o indicador conta só o que já se sabe.
  const resumo = useResumoDosCadastros();

  const hubs = useMemo(
    () =>
      AREA_ADMIN.grupos
        .flatMap((grupo) => grupo.itens)
        .filter(
          (item): item is Hub =>
            item.caminho.startsWith(`${RAIZ}/`) && (item.filhos?.length ?? 0) > 0,
        ),
    [],
  );

  const totalDeTelas = hubs.reduce((soma, hub) => soma + hub.filhos.length, 0);

  const totalDeRegistros = useMemo(() => {
    // Um domínio citado por dois hubs (cursos obrigatórios) conta uma vez só.
    const vistos = new Set<ChaveDeContagem>();
    for (const hub of hubs) {
      for (const dominio of HUBS[hub.caminho]?.dominios ?? []) {
        vistos.add(dominio);
      }
    }
    return [...vistos].reduce(
      (soma, dominio) => soma + (contagemDoDominio(dominio, resumo.data)?.ativos ?? 0),
      0,
    );
  }, [hubs, resumo.data]);

  const termo = busca.trim().toLowerCase();

  /** Busca filtra TELAS, não hubs: some o hub que não tem nenhuma tela correspondente. */
  const hubsFiltrados = useMemo(() => {
    if (termo.length < 2) {
      return hubs.map((hub) => ({ hub, telas: hub.filhos }));
    }
    return hubs
      .map((hub) => ({
        hub,
        telas: hub.filhos.filter((filho) => filho.titulo.toLowerCase().includes(termo)),
      }))
      .filter((entrada) => entrada.telas.length > 0);
  }, [hubs, termo]);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Centro de Parametrização</h1>
        <p className="text-sm text-muted-foreground">
          Os parâmetros que o sistema usa para validar cadastro, calcular remuneração e cobrar
          documento. Alterar aqui muda o comportamento de todos os módulos.
        </p>
      </header>

      {/*
        Dois indicadores, com o rótulo e a linha de apoio do protótipo. Ele tem
        quatro; os outros dois — "Alterações nos últimos 7 dias" e "Saúde do
        módulo" — estão ausentes pelo motivo explicado no cabeçalho deste
        arquivo, e não por esquecimento de layout.
      */}
      <dl className="grid gap-4 sm:grid-cols-2">
        <Indicador
          icone={LayoutGrid}
          rotulo="Áreas de parametrização"
          valor={hubs.length}
          apoio={`${totalDeTelas} telas disponíveis`}
        />
        <Indicador
          icone={Database}
          rotulo="Registros ativos"
          valor={totalDeRegistros}
          apoio="Itens habilitados nas tabelas-base"
        />
      </dl>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Busca rápida em todos os parâmetros</CardTitle>
          <CardDescription>
            Procure pelo nome da tela — por exemplo &quot;glosa&quot;, &quot;escola&quot; ou
            &quot;veículo&quot;. Mínimo de duas letras.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Buscar tela de parametrização…"
              aria-label="Buscar tela de parametrização"
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <section aria-labelledby="areas-do-modulo" className="space-y-3">
        <h2 id="areas-do-modulo" className="text-sm font-semibold uppercase text-muted-foreground">
          Áreas do módulo
        </h2>

        {hubsFiltrados.length === 0 ? (
          <EmptyState
            variant="search"
            title="Nenhuma tela encontrada"
            description={`Nada corresponde a "${busca.trim()}". Tente outro termo.`}
            actionLabel="Limpar busca"
            onAction={() => setBusca('')}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {hubsFiltrados.map(({ hub, telas }) => {
              const Icone = hub.icone;
              const registros = (HUBS[hub.caminho]?.dominios ?? []).reduce(
                (soma, dominio) => soma + (contagemDoDominio(dominio, resumo.data)?.total ?? 0),
                0,
              );

              return (
                <Card key={hub.caminho} className="flex flex-col">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
                        aria-hidden="true"
                      >
                        <Icone className="h-5 w-5" />
                      </span>
                      <div className="space-y-1">
                        {/*
                          O protótipo numera as áreas do módulo — `1. Cadastros
                          & Domínios` até `7. Governança & Assinaturas`. A
                          numeração acompanha a ordem do mapa de navegação, e
                          por isso continua certa quando a busca filtra: ela sai
                          da posição no mapa, não da posição na lista filtrada.
                        */}
                        <CardTitle className="text-base">
                          {`${hubs.indexOf(hub) + 1}. ${hub.titulo}`}
                        </CardTitle>
                        {/*
                          Hub sem entrada em HUBS ainda tem resumo: o do mapa de
                          navegação. Prazos e Workflows são assim — não reúnem
                          tabela-base, e por isso não aparecem naquele arquivo.
                        */}
                        <CardDescription>{HUBS[hub.caminho]?.resumo ?? hub.resumo}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="mt-auto space-y-3">
                    <ul className="flex flex-wrap gap-1.5">
                      {telas.slice(0, 3).map((tela) => (
                        <li key={tela.caminho}>
                          <Badge variant="secondary" className="font-normal">
                            {tela.titulo}
                          </Badge>
                        </li>
                      ))}
                      {telas.length > 3 && (
                        <li>
                          <Badge variant="outline" className="font-normal">
                            e mais {telas.length - 3}
                          </Badge>
                        </li>
                      )}
                    </ul>

                    {/* Hub que não reúne tabela-base não anuncia "0 registros". */}
                    {registros > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {registros} {registros === 1 ? 'registro' : 'registros'} nas tabelas-base
                      </p>
                    )}

                    <Link
                      to={hub.caminho}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                      Abrir {hub.titulo.toLowerCase()}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function Indicador({
  icone: Icone,
  rotulo,
  valor,
  apoio,
}: {
  icone: typeof LayoutGrid;
  rotulo: string;
  valor: number;
  /** Linha de apoio abaixo do número, como no protótipo. */
  apoio?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
          aria-hidden="true"
        >
          <Icone className="h-5 w-5" />
        </span>
        <div>
          <dt className="text-xs text-muted-foreground">{rotulo}</dt>
          <dd className="text-2xl font-semibold tabular-nums">{valor}</dd>
          {apoio && <dd className="text-xs text-muted-foreground">{apoio}</dd>}
        </div>
      </CardContent>
    </Card>
  );
}

export default CentroDeParametrizacao;
